import type {
  DynamicFieldValue,
  DynamicFieldValues,
  EmrCodeDynamicField,
  EmrCodeFieldBindings,
  EmrCodeFieldRow,
  EmrCodeFieldType,
  EmrCodeFields,
} from 'features/Procedure/state/procedure-api.type'

const OPTION_FIELD_TYPES: EmrCodeFieldType[] = [
  'dropdown',
  'multiselect',
  'checkboxgroup',
  'radiogroup',
]

const SINGLE_OPTION_FIELD_TYPES: EmrCodeFieldType[] = ['dropdown', 'radiogroup']

export type DynamicFieldOption = { value: string; label: string }

export type DynamicFieldKeyValue = Record<string, string>

export type DynamicFieldsDto = Record<
  string,
  string | number | string[] | DynamicFieldKeyValue[] | null
>

export const getDynamicFieldName = (field: EmrCodeDynamicField): string => field.name ?? field.label

const isFieldRow = (entry: EmrCodeDynamicField | EmrCodeFieldRow): entry is EmrCodeFieldRow =>
  typeof entry === 'object' && entry !== null && 'elements' in entry

export const toDynamicFieldRows = (fields?: EmrCodeFields): EmrCodeFieldRow[] => {
  if (!fields?.length) {
    return []
  }
  if (isFieldRow(fields[0])) {
    return (fields as EmrCodeFieldRow[]).filter((row) => Array.isArray(row?.elements))
  }
  return [{ elements: fields as EmrCodeDynamicField[] }]
}

export const flattenDynamicFields = (fields?: EmrCodeFields): EmrCodeDynamicField[] =>
  toDynamicFieldRows(fields).flatMap((row) => row.elements)

export const BOOTSTRAP_GRID_COLUMNS = 12
const ANTD_GRID_COLUMNS = 24

export const getColSpan = (col?: string): number | undefined => {
  const match = /^col-(\d{1,2})$/.exec(col?.trim() ?? '')
  if (!match) {
    return undefined
  }
  const size = Number(match[1])
  if (size < 1 || size > BOOTSTRAP_GRID_COLUMNS) {
    return undefined
  }
  return size * (ANTD_GRID_COLUMNS / BOOTSTRAP_GRID_COLUMNS)
}

export const isArrayBindings = (bindings?: EmrCodeFieldBindings): bindings is string[] =>
  Array.isArray(bindings)

const getBindingsLength = (bindings?: EmrCodeFieldBindings): number => {
  if (!bindings) {
    return 0
  }
  return isArrayBindings(bindings) ? bindings.length : Object.keys(bindings).length
}

export const getBindingOptions = (bindings?: EmrCodeFieldBindings): DynamicFieldOption[] => {
  if (!bindings) {
    return []
  }
  if (isArrayBindings(bindings)) {
    return bindings.map((binding) => ({ value: binding, label: binding }))
  }
  return Object.entries(bindings).map(([value, label]) => ({ value, label }))
}

export const isRenderableField = (field: EmrCodeDynamicField): boolean =>
  !OPTION_FIELD_TYPES.includes(field.type) || getBindingsLength(field.bindings) > 0

export const isDynamicFieldValueEmpty = (value: DynamicFieldValue): boolean => {
  if (value === undefined || value === null) {
    return true
  }
  if (typeof value === 'string') {
    return value.trim() === ''
  }
  if (Array.isArray(value)) {
    return value.length === 0
  }
  return false
}

export const getMissingRequiredDynamicFields = (
  fields: EmrCodeFields | undefined,
  values: DynamicFieldValues | undefined,
): EmrCodeDynamicField[] =>
  flattenDynamicFields(fields)
    .filter(isRenderableField)
    .filter(
      (field) => field.required && isDynamicFieldValueEmpty(values?.[getDynamicFieldName(field)]),
    )

const NUMERIC_FIELD_TYPES: EmrCodeFieldType[] = ['integer', 'number']

const toSelectedKeys = (rawValue: DynamicFieldValue): string[] => {
  if (Array.isArray(rawValue)) {
    return rawValue.map(String)
  }
  if (rawValue === undefined || rawValue === null || rawValue === '') {
    return []
  }
  return [String(rawValue)]
}

const buildFieldSubmitValue = (
  field: EmrCodeDynamicField,
  rawValue: DynamicFieldValue,
): DynamicFieldsDto[string] => {
  const bindings = field.bindings
  if (isArrayBindings(bindings)) {
    if (Array.isArray(rawValue)) {
      return rawValue
    }
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      return null
    }
    return [String(rawValue)]
  }
  if (bindings) {
    const selected = toSelectedKeys(rawValue)
    return selected.length ? selected.map((key) => ({ [key]: bindings[key] ?? key })) : null
  }
  if (NUMERIC_FIELD_TYPES.includes(field.type)) {
    return rawValue === undefined || rawValue === null || rawValue === '' ? null : String(rawValue)
  }
  return rawValue ?? null
}

export const buildDynamicFieldsDto = (
  fields: EmrCodeFields | undefined,
  values: DynamicFieldValues | undefined,
): DynamicFieldsDto =>
  Object.fromEntries(
    flattenDynamicFields(fields)
      .filter(isRenderableField)
      .map((field) => [
        getDynamicFieldName(field),
        buildFieldSubmitValue(field, values?.[getDynamicFieldName(field)]),
      ]),
  )

const MULTI_VALUE_FIELD_TYPES: EmrCodeFieldType[] = ['multiselect', 'checkboxgroup']

export const tryParseJson = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value
  }
  try {
    return JSON.parse(value) as unknown
  } catch {
    return value
  }
}

export const toIntegerValue = (rawValue: unknown): number | undefined => {
  const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue)
  return Number.isFinite(numericValue) ? Math.trunc(numericValue) : undefined
}

export const NUMBER_FIELD_MAX_DECIMALS = 4

// Float number inputs show two decimals (e.g. 10.15) and step by the last digit.
export const NUMBER_FIELD_DISPLAY_DECIMALS = 2

export const NUMBER_FIELD_STEP = 10 ** -NUMBER_FIELD_DISPLAY_DECIMALS

const DECIMALS_FACTOR = 10 ** NUMBER_FIELD_MAX_DECIMALS

export const toDecimalValue = (rawValue: unknown): number | undefined => {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return undefined
  }
  const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue)
  if (!Number.isFinite(numericValue)) {
    return undefined
  }
  return Math.round(numericValue * DECIMALS_FACTOR) / DECIMALS_FACTOR
}

export const countDecimals = (value: number): number => {
  if (Number.isInteger(value)) {
    return 0
  }
  const [, decimals = ''] = String(value).split('.')
  return decimals.length
}

const extractOptionKeys = (rawValue: unknown): string[] => {
  const parsedValue = tryParseJson(rawValue)
  const entries = Array.isArray(parsedValue) ? parsedValue : [parsedValue]
  return entries
    .map((entry) => {
      if (entry !== null && typeof entry === 'object' && !Array.isArray(entry)) {
        const [firstKey] = Object.keys(entry as Record<string, unknown>)
        return firstKey
      }
      return entry === undefined || entry === null ? undefined : String(entry)
    })
    .filter((key): key is string => key !== undefined)
}

const normalizeSourceValue = (field: EmrCodeDynamicField, rawValue: unknown): DynamicFieldValue => {
  if (MULTI_VALUE_FIELD_TYPES.includes(field.type)) {
    return extractOptionKeys(rawValue)
  }
  if (field.type === 'integer') {
    return toIntegerValue(rawValue)
  }
  if (field.type === 'number') {
    return toDecimalValue(rawValue)
  }
  if (SINGLE_OPTION_FIELD_TYPES.includes(field.type)) {
    const [firstKey] = extractOptionKeys(rawValue)
    return firstKey
  }
  if (typeof rawValue === 'number') {
    return rawValue
  }
  return String(rawValue)
}

export const extractDynamicFieldValues = (
  fields: EmrCodeFields | undefined,
  source: Record<string, unknown>,
): DynamicFieldValues =>
  Object.fromEntries(
    flattenDynamicFields(fields)
      .filter(isRenderableField)
      .map((field) => ({ field, rawValue: source[getDynamicFieldName(field)] }))
      .filter(({ rawValue }) => rawValue !== undefined && rawValue !== null && rawValue !== '')
      .map(({ field, rawValue }) => [
        getDynamicFieldName(field),
        normalizeSourceValue(field, rawValue),
      ])
      .filter(([, value]) => value !== undefined),
  )

export const getNonIntegerDynamicFields = (
  fields: EmrCodeFields | undefined,
  values: DynamicFieldValues | undefined,
): EmrCodeDynamicField[] =>
  flattenDynamicFields(fields)
    .filter(isRenderableField)
    .filter((field) => field.type === 'integer')
    .filter((field) => {
      const value = values?.[getDynamicFieldName(field)]
      if (isDynamicFieldValueEmpty(value)) {
        return false
      }
      return !Number.isInteger(typeof value === 'number' ? value : Number(value))
    })

export const getInvalidNumberDynamicFields = (
  fields: EmrCodeFields | undefined,
  values: DynamicFieldValues | undefined,
): EmrCodeDynamicField[] =>
  flattenDynamicFields(fields)
    .filter(isRenderableField)
    .filter((field) => field.type === 'number')
    .filter((field) => {
      const value = values?.[getDynamicFieldName(field)]
      if (isDynamicFieldValueEmpty(value)) {
        return false
      }
      const numericValue = typeof value === 'number' ? value : Number(value)
      if (!Number.isFinite(numericValue)) {
        return true
      }
      return countDecimals(numericValue) > NUMBER_FIELD_MAX_DECIMALS
    })

export const STRING_FIELD_MAX_LENGTH = 255

export const getTooLongStringDynamicFields = (
  fields: EmrCodeFields | undefined,
  values: DynamicFieldValues | undefined,
): EmrCodeDynamicField[] =>
  flattenDynamicFields(fields)
    .filter(isRenderableField)
    .filter((field) => field.type === 'string')
    .filter((field) => {
      const value = values?.[getDynamicFieldName(field)]
      return typeof value === 'string' && value.length > STRING_FIELD_MAX_LENGTH
    })
